import {setLimitsQuery, useBookableResourcesQuery, useEventTypesQuery} from '../../lib/Queries';
import { Cog6ToothIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import Modal from '../Modal';
import TextInput from '../TextInput';
import Select from '../Select';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import CompactEventTypeSelect from '../forms/CompactEventTypeSelect';
import CompactBookableResourcesSelect from "~/components/scheduling/CompactBookableResourcesSelect";

const EXCEPTION_TYPE_BOOKABLE_RESOURCE = 'bookableresource';

export const EventTypeHeading = ({ typeIds, className }) => {
    const { data, isLoading } = useEventTypesQuery();
    const allTypes = data || [];
    typeIds = (typeIds || []).map(id => parseInt(id));

    let types = '';

    const isAllTypes =
        allTypes.filter(t => typeIds?.indexOf(t.id) < 0).length === 0;
    const typeNames = allTypes
        .filter(t => typeIds?.indexOf(t.id) > -1)
        .map(t => t.name);

    if (isAllTypes) {
        types = 'All event types';
    } else if (typeIds?.length < 8) {
        // Just a few, list them
        types = typeNames.join(', ');
    } else {
        // Too many!
        types = `${typeNames[0]} +${typeNames.length - 1} more types`;
    }

    return (
        <div className={!className ? 'font-semibold mb-1' : className}>
            {types}
        </div>
    );
};


export const BookableResourcesHeading = ({ resourceIds, className }) => {
    const { data, isLoading } = useBookableResourcesQuery();
    const allResources = data || [];
    resourceIds = (resourceIds || []).map(id => parseInt(id));

    let types = '';

    const isAllTypes =
        allResources.filter(t => resourceIds?.indexOf(t.id) < 0).length === 0;
    const typeNames = allResources
        .filter(t => resourceIds?.indexOf(t.id) > -1)
        .map(t => t.name);

    if (isAllTypes) {
        types = 'Everyone';
    } else if (resourceIds?.length < 8) {
        // Just a few, list them
        types = typeNames.join(', ');
    } else {
        // Too many!
        types = `${typeNames[0]} +${typeNames.length - 1} more`;
    }

    return (
        <div className={!className ? 'font-semibold mb-1' : className}>
            {types}
        </div>
    );
};


const LimitException = ({ exception, setException, isBookableResourceException, LimitInput }) => {
    exception = exception || {};

    return (
        <div className="flex flex-col mb-4 xs:flex-row xs:items-center xs:gap-4 xs:mb-0">
            <div className="mb-4 panel py-3 px-4 flex-1">
                {isBookableResourceException && (
                    <CompactBookableResourcesSelect
                        className="mb-2 border"
                        value={exception.bookableResources}
                        dontDefaultAll={true}
                        onChange={newValue =>
                            setException({ ...exception, bookableResources: newValue })
                        }
                    />
                )}
                {!isBookableResourceException && (
                    <CompactEventTypeSelect
                        className="mb-2 border"
                        value={exception.types}
                        onChange={newValue =>
                            setException({ ...exception, types: newValue })
                        }
                    />
                )}


                <LimitInput
                    limit={exception.limit}
                    setLimit={newLimit =>
                        setException({ ...exception, limit: newLimit })
                    }
                />
            </div>

            <button
                className="btn-pink-pill flex items-center justify-center p-2 w-full xs:w-10 xs:h-10 xs:p-0"
                type="button"
                onClick={() => setException(null)}
            >
                <TrashIcon className="w-5 h-5" />
                <span className="inline xs:hidden ml-2">Remove</span>
            </button>
        </div>
    );
};

const LimitSettingsModal = ({
    isOpen,
    onClose,
    doSave,
    initialSettings,
    title,
    LimitInput,
}) => {
    const [settings, setSettings] = useState(initialSettings);
    const [hasSetInitialSettings, setHasSetInitialSettings] = useState(false);
    const exceptions = settings?.exceptions || [];
    const [isSaving, setIsSaving] = useState(false);
    const isBookableResourceException = settings?.exceptionType === EXCEPTION_TYPE_BOOKABLE_RESOURCE;

    useEffect(() => {
        if (isOpen) {
            if (initialSettings) setSettings(initialSettings);
            setHasSetInitialSettings(true);
        }

        if (!isOpen) {
            setHasSetInitialSettings(false);
        }
    }, [isOpen]);

    function setExceptions(newExceptions) {
        setSettings({ ...settings, exceptions: newExceptions });
    }

    async function save() {
        try {
            setIsSaving(true);
            await doSave(settings);
            onClose();
        } catch (e) {
            // ...
        }

        setIsSaving(false);
    }

    return (
        <Modal
            isOpen={isOpen && hasSetInitialSettings}
            onClose={onClose}
            widthClass="sm:max-w-[700px]"
        >
            <h2 className="mb-6">{title}</h2>
            {!!exceptions?.length && (
                <div className="panel-label">Default Limit</div>
            )}

            <div className="panel py-3 px-4">
                <LimitInput limit={settings} setLimit={setSettings} />
            </div>

            <div className="-mx-6 px-6 mt-4 pt-4">
                {exceptions.length > 0 && (
                    <div>
                        <div className="panel-label">Exceptions</div>
                        <Select
                            onChange={(newValue) => setSettings((old) => ({...old, exceptionType: newValue}))}
                            value={settings?.exceptionType}
                            options={[
                                {value: 'eventType', label: 'By Event Type'},
                                {value: EXCEPTION_TYPE_BOOKABLE_RESOURCE, label: 'By Person'},
                            ]}
                            className="mb-4"
                            inputClassName="w-full xs:w-auto"
                        />
                    </div>
                )}
                {exceptions.map((exception, idx) => (
                    <LimitException
                        key={idx}
                        exception={exception}
                        isBookableResourceException={isBookableResourceException}
                        LimitInput={LimitInput}
                        setException={newException => {
                            const newExceptions = [...exceptions];
                            if (newException) {
                                newExceptions[idx] = newException;
                            } else {
                                newExceptions.splice(idx, 1);
                            }
                            setExceptions(newExceptions);
                        }}
                    />
                ))}
                <button
                    className="btn-light-pill w-full xs:w-auto"
                    onClick={() => setExceptions([...exceptions, {}])}
                >
                    Add Exception
                </button>
            </div>
            <div className="-mx-6 px-6 border-t mt-4 pt-4">
                <button className="btn-save w-full xs:w-auto" onClick={save}>
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
            </div>
        </Modal>
    );
};

const LimitsContainer = ({
    title,
    limit,
    type,
    exceptionsLabel,
    LimitDescription,
    LimitInput,
    CustomModal,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { save, isSaving } = useBusinessPostApi(`/scheduling/limits/${type}`);
    async function doSave(newSettings) {
        const data = await save({
            settings: newSettings,
        });
        setLimitsQuery(data);
    }

    const isBookableResourceException = limit?.exceptionType === EXCEPTION_TYPE_BOOKABLE_RESOURCE;
    const hasExceptions =
        limit?.exceptions?.filter(e => {
            if (isBookableResourceException) {
                return !!e?.bookableResources?.length;
            } else {
                return !!e?.types?.length
            }
        }).length > 0;

    const MyModal = CustomModal || LimitSettingsModal;

    return (
        <div>
            <div
                className="cursor-pointer"
                onClick={() => setIsModalOpen(true)}
            >
                <div className="flex justify-between items-start">
                    <LimitDescription
                        limit={limit}
                        isDefault={true}
                        hasExceptions={hasExceptions}
                    />
                    <button
                        className="btn-gray-link ml-4"
                        onClick={() => setIsModalOpen(true)}
                    >
                        <Cog6ToothIcon className="w-5 h-5" />
                    </button>
                </div>
                {!!limit?.exceptions && hasExceptions && (
                    <div>
                        <div className="mt-4 panel-label">
                            {exceptionsLabel || 'Exceptions'}
                        </div>
                        {limit.exceptions.map((exception, idx) => (
                            <>
                                {(
                                    (!isBookableResourceException && !!exception.types?.length)
                                    ||
                                    (isBookableResourceException && !!exception.bookableResources?.length)
                                ) && (
                                    <div
                                        className={`panel-gray p-4 ${idx > 0 && 'mt-4'}`}
                                        key={idx}
                                    >
                                        {isBookableResourceException && (
                                            <BookableResourcesHeading
                                                resourceIds={exception.bookableResources}
                                            />
                                        )}
                                        {!isBookableResourceException && (
                                            <EventTypeHeading
                                                typeIds={exception.types}
                                            />
                                        )}

                                        <LimitDescription
                                            limit={exception.limit}
                                        />
                                    </div>
                                )}
                            </>
                        ))}
                    </div>
                )}
            </div>

            <MyModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                doSave={doSave}
                initialSettings={limit}
                LimitInput={LimitInput}
                title={title}
            />
        </div>
    );
};

export default LimitsContainer;
